import React, { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { X, Plus } from "lucide-react";

// Quick-Add product from an unknown-barcode scan. Barcode is prefilled as SKU.
// On success calls onCreated(item) with the enriched product (same shape as /pos/lookup items).
export const QuickAddModal = ({ code, onCreated, onClose }) => {
  const [sku, setSku] = useState(code || "");
  const [name, setName] = useState("");
  const [price, setPrice] = useState("");
  const [qty, setQty] = useState("1");
  const [type, setType] = useState("Merchandise");
  const [compareAt, setCompareAt] = useState("");
  const [cost, setCost] = useState("");
  const [receipt, setReceipt] = useState("");
  const [more, setMore] = useState(false);
  const [err, setErr] = useState("");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setErr("");
    if (!sku.trim()) { setErr("SKU is required"); return; }
    if (!name.trim()) { setErr("Product name is required"); return; }
    const p = parseFloat(price);
    if (isNaN(p) || p < 0) { setErr("Enter a valid price"); return; }
    const q = parseInt(qty, 10);
    if (isNaN(q) || q < 0) { setErr("Enter a valid quantity"); return; }
    setSaving(true);
    try {
      const r = await api.post("/pos/quick-add", {
        sku: sku.trim(), product_name: name.trim(), price: p, qty: q, product_type: type,
        compare_at_price: compareAt === "" ? null : parseFloat(compareAt),
        cost: cost === "" ? null : parseFloat(cost),
        purchase_receipt: receipt.trim(),
      });
      toast.success(`Created ${r.data.product_name}`);
      onCreated?.(r.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not create product");
    }
    setSaving(false);
  };

  const fld = "w-full h-11 border border-slate-200 rounded-lg px-3 text-sm outline-none focus:border-brand-navy";
  return (
    <div className="fixed inset-0 z-[100] bg-black/60 grid place-items-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-md max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="quick-add-modal">
        <div className="flex items-center justify-between px-5 py-4 border-b sticky top-0 bg-white">
          <h2 className="font-heading text-xl text-brand-navy">New product</h2>
          <button onClick={onClose} data-testid="quick-add-close"><X className="h-5 w-5 text-slate-500" /></button>
        </div>
        <div className="p-5 space-y-3">
          <label className="block">
            <span className="text-xs font-bold text-slate-600">SKU / barcode</span>
            <input value={sku} onChange={(e) => setSku(e.target.value)} data-testid="quick-add-sku" className={`${fld} mt-1 font-mono`} />
          </label>
          <label className="block">
            <span className="text-xs font-bold text-slate-600">Product name *</span>
            <input autoFocus value={name} onChange={(e) => setName(e.target.value)} data-testid="quick-add-name" className={`${fld} mt-1`} placeholder="e.g. Kambukka 2PK Mug" />
          </label>
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="text-xs font-bold text-slate-600">Our price *</span>
              <input type="number" min="0" step="0.01" value={price} onChange={(e) => setPrice(e.target.value)} data-testid="quick-add-price" className={`${fld} mt-1 text-right`} placeholder="0.00" />
            </label>
            <label className="block">
              <span className="text-xs font-bold text-slate-600">Quantity *</span>
              <input type="number" min="0" step="1" value={qty} onChange={(e) => setQty(e.target.value)} data-testid="quick-add-qty" className={`${fld} mt-1 text-right`} />
            </label>
          </div>
          <label className="block">
            <span className="text-xs font-bold text-slate-600">Type</span>
            <select value={type} onChange={(e) => setType(e.target.value)} data-testid="quick-add-type" className={`${fld} mt-1`}>
              <option value="Merchandise">Merchandise</option>
              <option value="Clothing">Clothing</option>
            </select>
          </label>
          <button onClick={() => setMore((v) => !v)} data-testid="quick-add-more" className="text-sm text-brand-navy font-bold">{more ? "Hide" : "Add"} optional details</button>
          {more && (
            <div className="grid grid-cols-2 gap-3">
              <label className="block">
                <span className="text-xs font-bold text-slate-600">Compare-at price</span>
                <input type="number" min="0" step="0.01" value={compareAt} onChange={(e) => setCompareAt(e.target.value)} data-testid="quick-add-compare" className={`${fld} mt-1 text-right`} placeholder="optional" />
              </label>
              <label className="block">
                <span className="text-xs font-bold text-slate-600">Cost</span>
                <input type="number" min="0" step="0.01" value={cost} onChange={(e) => setCost(e.target.value)} data-testid="quick-add-cost" className={`${fld} mt-1 text-right`} placeholder="optional" />
              </label>
              <label className="block col-span-2">
                <span className="text-xs font-bold text-slate-600">Purchase receipt</span>
                <input value={receipt} onChange={(e) => setReceipt(e.target.value)} data-testid="quick-add-receipt" className={`${fld} mt-1`} placeholder="optional" />
              </label>
            </div>
          )}
          {err && <div className="text-sm text-brand-red font-bold" data-testid="quick-add-error">{err}</div>}
        </div>
        <div className="px-5 py-4 border-t sticky bottom-0 bg-white flex justify-end gap-2">
          <button onClick={onClose} className="h-11 px-4 rounded-lg border border-slate-200 text-slate-600 font-bold">Cancel</button>
          <button onClick={save} disabled={saving} data-testid="quick-add-save" className="h-11 px-5 rounded-lg bg-brand-green text-white font-bold inline-flex items-center gap-2 disabled:opacity-60"><Plus className="h-4 w-4" /> {saving ? "Saving…" : "Create"}</button>
        </div>
      </div>
    </div>
  );
};
